import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { createPortal } from 'react-dom';
import { getFullscreenPortalTarget } from './fullscreenPortal';
import { FaDoorOpen, FaTimes, FaCheck, FaExternalLinkAlt, FaVideo, FaLock, FaCalendarPlus } from 'react-icons/fa';
import API from '../../API';
import { useOverlay } from '../../contexts/FocusContext';
import UnityGameDockApp from './UnityGameDockApp';
import { BookRoomModal } from './BookRoom';

interface Room {
  room_id: string;
  room_name: string;
  event_id: string;
  chat_room_arn: string;
  stage_arn: string;
}

/** Matches Agenda / BookRoom booking shape from GET /bookings/:eventId */
interface RoomBooking {
  id: string;
  room_id: string;
  title: string;
  start_time: string;
  end_time: string;
  status: string;
  is_private?: boolean;
}

const MAIN_ROOM_ID = 'intersect-main';

function getBookingLiveStatus(booking: RoomBooking): 'upcoming' | 'ongoing' | 'completed' {
  const now = new Date();
  const startTime = new Date(booking.start_time);
  const endTime = new Date(booking.end_time);
  if (now < startTime) return 'upcoming';
  if (now >= startTime && now <= endTime) return 'ongoing';
  return 'completed';
}

function formatTimeUtc(utcIso: string): string {
  const date = new Date(utcIso);
  const h = date.getUTCHours().toString().padStart(2, '0');
  const m = date.getUTCMinutes().toString().padStart(2, '0');
  return `${h}:${m} UTC`;
}

function isBreakoutRoomId(roomId: string): boolean {
  return roomId !== MAIN_ROOM_ID;
}

function pickOngoingBookingForRoom(bookings: RoomBooking[], roomId: string): RoomBooking | null {
  const ongoing = bookings.filter(
    (b) =>
      b.room_id === roomId &&
      b.status !== 'cancelled' &&
      getBookingLiveStatus(b) === 'ongoing'
  );
  if (ongoing.length === 0) return null;
  ongoing.sort((a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime());
  return ongoing[0];
}

function LiveBookingListSubtitle({ ongoing }: { ongoing: RoomBooking }) {
  return (
    <div className="text-[#aaa]">
      <span className="font-medium text-green-400/90">Live booking</span>
      <span className="text-gray-300"> — {ongoing.title}</span>
      {ongoing.is_private ? (
        <FaLock className="ml-1 inline align-middle text-amber-400/90" size={10} title="Private" />
      ) : null}
      <span className="mt-0.5 block text-[11px] text-[#666]">Until {formatTimeUtc(ongoing.end_time)}</span>
    </div>
  );
}

interface RoomSelectorProps {
  accessToken: string;
  eventId: string;
  currentRoomId: string;
  onRoomChange: (room: Room) => void;
  cachedRooms?: Room[]; // Use cached rooms from parent
  isLoadingRooms?: boolean; // Loading state from parent
  onCodeUnlocked?: (code: string) => void; // When user joins by code, add to shared unlocked list
  onRoomChangeDirect?: (room: Room) => void; // Bypass private gate when already validated by code
  /** Use dock squircle + popover above (Unity game); false = legacy top toolbar button */
  dockMode?: boolean;
  /** Staff can book main room in BookRoomModal */
  isStaff?: boolean;
}

export interface RoomSelectorRef {
  open: () => void;
  close: () => void;
  toggle: () => void;
}

const RoomSelector = forwardRef<RoomSelectorRef, RoomSelectorProps>(({ 
  accessToken, 
  eventId, 
  currentRoomId,
  onRoomChange,
  cachedRooms = [],
  isLoadingRooms = false,
  onCodeUnlocked,
  onRoomChangeDirect,
  dockMode = true,
  isStaff = false,
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showBookRoomModal, setShowBookRoomModal] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<Room | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [roomCodeInput, setRoomCodeInput] = useState('');
  const [joinByCodeError, setJoinByCodeError] = useState<string | null>(null);
  const [isJoiningByCode, setIsJoiningByCode] = useState(false);
  const [bookings, setBookings] = useState<RoomBooking[]>([]);
  const [isLoadingBookings, setIsLoadingBookings] = useState(false);
  const [bookingsFetchFailed, setBookingsFetchFailed] = useState(false);

  const fetchBookingsForRooms = useCallback(async () => {
    if (!accessToken || !eventId) return;
    setIsLoadingBookings(true);
    setBookingsFetchFailed(false);
    try {
      const response = await API.getBookings(accessToken, eventId);
      if (response.status !== 200) {
        setBookings([]);
        setBookingsFetchFailed(true);
        return;
      }
      const data = await response.json();
      const list: RoomBooking[] = data.bookings || [];
      setBookings(list.filter((b) => b.status !== 'cancelled'));
    } catch {
      setBookings([]);
      setBookingsFetchFailed(true);
    } finally {
      setIsLoadingBookings(false);
    }
  }, [accessToken, eventId]);

  useEffect(() => {
    if (isOpen) {
      fetchBookingsForRooms();
    }
  }, [isOpen, fetchBookingsForRooms]);

  useEffect(() => {
    if (!isOpen) setShowBookRoomModal(false);
  }, [isOpen]);

  // Register as overlay when panel, confirm dialog, or book modal is open
  useOverlay('room-selector', isOpen || showConfirmDialog || showBookRoomModal);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    open: () => setIsOpen(true),
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev)
  }));

  // Use cached rooms from parent, no need to fetch
  const rooms = cachedRooms;

  const handleRoomClick = (room: Room) => {
    if (room.room_id === currentRoomId) return; // Already in this room
    setSelectedRoom(room);
    setShowConfirmDialog(true);
  };

  const handleConfirmJoin = () => {
    if (selectedRoom) {
      onRoomChange(selectedRoom);
      setShowConfirmDialog(false);
      setSelectedRoom(null);
      setIsOpen(false);
    }
  };

  const handleCancelJoin = () => {
    setShowConfirmDialog(false);
    setSelectedRoom(null);
  };

  // Links and moderator editing are resolved by the shared CallRoomAccess launcher.
  const callRooms = [
    { id: 1, name: '[Call Room] 1' },
    { id: 2, name: '[Call Room] 2' },
    { id: 3, name: '[Call Room] 3' },
  ];

  const handleCallRoomClick = (id: number) => {
    setIsOpen(false);
    window.dispatchEvent(new CustomEvent('callRoomRequested', { detail: String(id) }));
  };

  const handleJoinByCode = async () => {
    const code = roomCodeInput.trim();
    if (!code) return;
    setJoinByCodeError(null);
    setIsJoiningByCode(true);
    try {
      const response = await API.getBookingByCode(accessToken, eventId, code);
      if (response.status === 200) {
        const booking = await response.json();
        const room = cachedRooms.find((r) => r.room_id === booking.room_id);
        if (room) {
          onCodeUnlocked?.(code);
          (onRoomChangeDirect ?? onRoomChange)(room);
          setRoomCodeInput('');
          setIsOpen(false);
        } else {
          setJoinByCodeError('Room not found for this event.');
        }
      } else {
        const text = await response.text();
        setJoinByCodeError(text || 'No booking found for this code.');
      }
    } catch {
      setJoinByCodeError('Failed to look up code. Try again.');
    } finally {
      setIsJoiningByCode(false);
    }
  };

  const roomListContent = (
    <>
      {isLoadingRooms ? (
        <div className="py-8 text-center text-[#666]">Loading rooms...</div>
      ) : rooms.length === 0 ? (
        <div className="py-8 text-center text-[#666]">No rooms available</div>
      ) : (
        <>
          {rooms.map((room) => {
            const isCurrentRoom = room.room_id === currentRoomId;
            const showBreakoutSubtitle = isBreakoutRoomId(room.room_id);
            const ongoing = pickOngoingBookingForRoom(bookings, room.room_id);
            const showMainLiveSubtitle =
              room.room_id === MAIN_ROOM_ID && !isLoadingBookings && !bookingsFetchFailed && !!ongoing;
            return (
              <button
                key={room.room_id}
                type="button"
                onClick={() => handleRoomClick(room)}
                disabled={isCurrentRoom}
                className={`w-full rounded-lg p-3 text-left transition-all duration-200 ${
                  isCurrentRoom
                    ? 'cursor-default border border-green-600 bg-green-600 bg-opacity-30'
                    : 'cursor-pointer border border-[#444] bg-[#2a2a2a] hover:border-[#555] hover:bg-[#333]'
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0 flex-1 font-medium text-white">{room.room_name}</div>
                  {isCurrentRoom && <FaCheck className="shrink-0 text-green-500" size={16} />}
                </div>
                {showBreakoutSubtitle && (
                  <div className="mt-1.5 text-xs leading-snug">
                    {isLoadingBookings ? (
                      <span className="text-[#666]">Checking bookings…</span>
                    ) : bookingsFetchFailed ? (
                      <span className="text-[#888]">Booking status unavailable</span>
                    ) : ongoing ? (
                      <LiveBookingListSubtitle ongoing={ongoing} />
                    ) : (
                      <span className="text-[#888]">Room open to book</span>
                    )}
                  </div>
                )}
                {showMainLiveSubtitle && ongoing && (
                  <div className="mt-1.5 text-xs leading-snug">
                    <LiveBookingListSubtitle ongoing={ongoing} />
                  </div>
                )}
              </button>
            );
          })}
          {rooms.length > 0 && (
            <div className="mt-2 border-t border-[#444] pt-2">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowBookRoomModal(true);
                }}
                className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600/90 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-emerald-600"
                title="Book a room"
              >
                <FaCalendarPlus size={16} />
                Book a room
              </button>
            </div>
          )}
          <div className="mt-2 border-t border-[#444] pt-2">
            <label className="mb-1 flex items-center gap-1 text-xs text-gray-400">
              <FaLock size={10} />
              Join by room code
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={roomCodeInput}
                onChange={(e) => {
                  setRoomCodeInput(e.target.value.toUpperCase());
                  setJoinByCodeError(null);
                }}
                placeholder="e.g. ABC123"
                className="flex-1 rounded border border-[#444] bg-[#2a2a2a] px-2 py-2 font-mono text-sm text-white placeholder-gray-500 focus:border-green-500 focus:outline-none"
                maxLength={12}
                disabled={isJoiningByCode}
              />
              <button
                type="button"
                onClick={handleJoinByCode}
                disabled={!roomCodeInput.trim() || isJoiningByCode}
                className="rounded bg-green-600 px-3 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {isJoiningByCode ? '...' : 'Join'}
              </button>
            </div>
            {joinByCodeError && <p className="mt-1 text-xs text-red-400">{joinByCodeError}</p>}
          </div>
          {rooms.length > 0 && <div className="my-2 border-t border-[#444]" />}
          {callRooms.map((callRoom) => (
            <button
              key={callRoom.id}
              type="button"
              onClick={() => handleCallRoomClick(callRoom.id)}
              className="w-full cursor-pointer rounded-lg border border-[#444] bg-[#2a2a2a] p-3 text-left transition-all duration-200 hover:border-blue-600 hover:bg-blue-900 hover:bg-opacity-30"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FaVideo className="text-blue-400" size={14} />
                  <div className="font-medium text-white">{callRoom.name}</div>
                </div>
                <FaExternalLinkAlt className="text-blue-400" size={12} />
              </div>
              <div className="ml-6 mt-1 text-xs text-[#666]">Google Meet · opens in a new tab</div>
            </button>
          ))}
        </>
      )}
    </>
  );

  const ongoingForJoinConfirm =
    showConfirmDialog && selectedRoom
      ? pickOngoingBookingForRoom(bookings, selectedRoom.room_id)
      : null;

  const panelHeader = (
    <div className="flex items-center justify-between border-b border-[#333] p-4">
      <h3 className="flex items-center gap-2 text-lg font-semibold text-white">
        <FaDoorOpen />
        Rooms
      </h3>
      <button
        type="button"
        onClick={() => setIsOpen(false)}
        className="text-[#999] transition-colors hover:text-white"
      >
        <FaTimes size={18} />
      </button>
    </div>
  );

  return (
    <>
      {dockMode ? (
        <UnityGameDockApp
          ariaLabel="Rooms — choose a space or join by code"
          label="Rooms"
          icon={<FaDoorOpen />}
          active={isOpen}
          onRequestClose={() => setIsOpen(false)}
          tileClassName="bg-gradient-to-br from-indigo-600 to-violet-700"
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
        >
          {isOpen && (
            <div
              className="unity-dock-popover unity-dock-popover--enter flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {panelHeader}
              <div className="min-h-0 flex-1 space-y-2 overflow-y-auto p-3">{roomListContent}</div>
            </div>
          )}
        </UnityGameDockApp>
      ) : (
        <div className="relative">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(!isOpen);
            }}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 font-semibold transition-all duration-200 ${
              isOpen
                ? 'bg-purple-700 bg-opacity-90 text-white'
                : 'bg-purple-600 bg-opacity-80 text-white hover:bg-opacity-100'
            }`}
            title="Room Selector"
          >
            <FaDoorOpen size={16} />
            Rooms
          </button>
          {isOpen && (
            <div
              className="absolute left-0 top-full z-50 mt-2 flex max-h-[70vh] w-80 flex-col rounded-lg border border-[#333] bg-[#1a1a1a] shadow-2xl"
              style={{ zIndex: 10001 }}
              onClick={(e) => e.stopPropagation()}
            >
              {panelHeader}
              <div className="flex-1 space-y-2 overflow-y-auto p-3">{roomListContent}</div>
            </div>
          )}
        </div>
      )}

      {showConfirmDialog &&
        selectedRoom &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            className="fixed inset-0 z-[10002] flex items-center justify-center bg-black/75"
            onClick={handleCancelJoin}
          >
            <div
              className="mx-4 w-full max-w-md rounded-lg border border-[#333] bg-[#1a1a1a] p-6"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="mb-4 text-xl font-semibold text-white">Join Room?</h3>
              <p className="mb-6 text-[#999]">
                Are you sure you want to join <strong className="text-white">{selectedRoom.room_name}</strong>?
                {!isLoadingBookings && !bookingsFetchFailed && (
                  <>
                    {ongoingForJoinConfirm ? (
                      <span className="mt-2 block rounded border border-green-500/30 bg-green-900/20 px-2 py-1.5 text-xs text-green-200/90">
                        Live booking: {ongoingForJoinConfirm.title}
                        {ongoingForJoinConfirm.is_private ? (
                          <FaLock className="ml-1 inline text-amber-400/90" size={10} />
                        ) : null}
                      </span>
                    ) : isBreakoutRoomId(selectedRoom.room_id) ? (
                      <span className="mt-2 block text-xs italic text-[#888]">Room open to book</span>
                    ) : null}
                  </>
                )}
                <br />
                <span className="mt-2 block text-xs text-[#666]">This will disconnect you from your current room.</span>
              </p>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={handleCancelJoin}
                  className="rounded-lg bg-[#2a2a2a] px-4 py-2 text-white transition-all duration-200 hover:bg-[#333]"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmJoin}
                  className="rounded-lg bg-green-600 px-4 py-2 text-white transition-all duration-200 hover:bg-green-700"
                >
                  Join Room
                </button>
              </div>
            </div>
          </div>,
          getFullscreenPortalTarget()
        )}

      <BookRoomModal
        open={showBookRoomModal}
        onClose={() => setShowBookRoomModal(false)}
        accessToken={accessToken}
        eventId={eventId}
        rooms={cachedRooms}
        isStaff={isStaff}
        onBookingCreated={() => {
          void fetchBookingsForRooms();
        }}
      />
    </>
  );
});

RoomSelector.displayName = 'RoomSelector';

export default RoomSelector;
